import { API_HOST, FALLBACK_PATH } from './config'
import type { ApiEnvelope } from './types'
import { fetchWithTimeout } from './support'

/**
 * The tutor's last resort: move this live class off LiveKit and onto Zoom.
 *
 * Like moderation, this goes to our API rather than to LiveKit, and the server
 * re-derives from the join credential that the caller really is this class's
 * teacher — the credential below is never a claim about who is asking.
 *
 * It is not undoable from here. Everyone in the room is disconnected, the
 * recording ends, and a pooled Zoom room is spent. The UI gates it behind a
 * confirmation and a reason for exactly that reason.
 */

export interface FallbackResult {
  provider: string
  changed: boolean
  meeting_url: string | null
  start_url: string | null
}

/**
 * Is this a URL we are willing to send a class of children to?
 *
 * This is a SECURITY gate, not a tidiness check. The Zoom link reaches clients
 * over the LiveKit data channel, and a data channel is something participants
 * can publish on. `useClassMoved` additionally refuses any packet that carries a
 * sender identity, but a single gate on a path that redirects children is not
 * enough — so the destination is also checked against the only host it is ever
 * legitimately allowed to be.
 */
export function isZoomUrl(value: unknown): value is string {
  // Bounded before parsing: URL() on a megabyte of attacker-supplied string is
  // work we have no reason to do.
  if (typeof value !== 'string' || value.length === 0 || value.length > 2048) return false

  let url: URL
  try {
    url = new URL(value)
  } catch {
    return false
  }

  if (url.protocol !== 'https:') return false

  const host = url.hostname.toLowerCase()
  // The dot matters: `evilzoom.us` must not pass, `ischool.zoom.us` must.
  return host === 'zoom.us' || host.endsWith('.zoom.us')
}

// Longer than moderation's 10s: the server provisions a real Zoom room inside
// this request, which is a call to a second vendor. Timing out early would leave
// the tutor believing it failed while the class was in fact being moved.
const REQUEST_TIMEOUT_MS = 25_000

export async function moveClassToZoom(
  credential: { code?: string | null; reconnectToken?: string | null },
  reason: string,
): Promise<FallbackResult> {
  const response = await fetchWithTimeout(
    `${API_HOST}${FALLBACK_PATH}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      cache: 'no-store',
      body: JSON.stringify({
        code: credential.code ?? undefined,
        reconnect_token: credential.reconnectToken ?? undefined,
        reason,
      }),
    },
    REQUEST_TIMEOUT_MS,
  )

  let envelope: ApiEnvelope<FallbackResult> | undefined
  try {
    envelope = (await response.json()) as ApiEnvelope<FallbackResult>
  } catch {
    /* classified below */
  }

  // Always surfaced. A tutor who taps this has already decided the lesson is
  // failing; silence would leave them guessing whether their class is about to
  // be disconnected.
  if (!response.ok || envelope?.status !== 'success') {
    throw new Error(envelope?.message ?? 'That did not work — try again')
  }
  return envelope.data
}
